import React, { useEffect, useState } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import type { NodeStats } from '@/types';
import toast from 'react-hot-toast';
import { notify } from '@/utils/notify';
import { useTranslation } from 'react-i18next';
import { formatBandwidth, formatHashPower, formatUptime } from '@/utils/format';
import { readOptionalProtocolFees } from '@/utils/rewardMath';
import { ethers } from 'ethers';
import '@/styles/hacker-aesthetic.css';
import '@/styles/inventory.css';

interface Upgrade {
  tokenId: string;
  upgradeName: string;
  statAffected: string;
  boostAmount: number;
  rarity: number;
}

interface ClosedChest {
  tokenId: string;
  chestTier: number;
  mintBlock: number;
  isOpened: boolean;
}

interface InventoryPanelProps {
  onClose: () => void;
  initialSelectedNodeId?: string | null;
}

const waitUntilBlockAfter = async (provider: any, mintBlock: number) => {
  let current = await provider.getBlockNumber();
  const revealBlock = mintBlock + 1;
  const started = Date.now();
  while (current <= revealBlock) {
    if (Date.now() - started > 120_000) {
      throw new Error('Timed out waiting for reveal block — try again shortly');
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
    current = await provider.getBlockNumber();
  }
};

export const InventoryPanel: React.FC<InventoryPanelProps> = ({
  onClose,
  initialSelectedNodeId = null,
}) => {
  const { contracts, signer, isWrongNetwork, contractsReady } = useNetwork();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [upgrades, setUpgrades] = useState<Upgrade[]>([]);
  const [chests, setChests] = useState<ClosedChest[]>([]);
  const [nodes, setNodes] = useState<NodeStats[]>([]);
  const [targetNodeId, setTargetNodeId] = useState<string | null>(initialSelectedNodeId);
  const [selectedUpgradeId, setSelectedUpgradeId] = useState<string | null>(null);
  const [nodeSearch, setNodeSearch] = useState('');
  const [upgradeSearch, setUpgradeSearch] = useState('');
  const [openingChestId, setOpeningChestId] = useState<string | null>(null);
  const [reactivationFee, setReactivationFee] = useState<string | null>(null);
  const [protocolFeeBps, setProtocolFeeBps] = useState<number | null>(null);

  useEffect(() => {
    if (signer && contracts) {
      void loadInventory();
    }
  }, [signer, contracts]);

  useEffect(() => {
    if (initialSelectedNodeId) {
      setTargetNodeId((current) => current ?? initialSelectedNodeId);
    }
  }, [initialSelectedNodeId]);

  const loadInventory = async () => {
    if (!signer || !contracts) return;

    try {
      setLoading(true);
      const address = await signer.getAddress();

      if (contracts.gameLogic) {
        try {
          const fees = await readOptionalProtocolFees(contracts.gameLogic);
          setReactivationFee(
            fees.reactivationFeeWei != null
              ? ethers.formatEther(fees.reactivationFeeWei)
              : null
          );
          setProtocolFeeBps(fees.protocolFeeBps);
        } catch {
          setReactivationFee(null);
          setProtocolFeeBps(null);
        }
      }

      if (contracts.upgradeNFT) {
        let tokenIds: any[] = [];
        try {
          tokenIds = await contracts.upgradeNFT.getUpgradesByOwner(address);
        } catch {
          const upgradeBalance = Number(await contracts.upgradeNFT.balanceOf(address));
          tokenIds = await Promise.all(
            Array.from({ length: upgradeBalance }, (_, i) =>
              contracts.upgradeNFT.tokenOfOwnerByIndex(address, i)
            )
          );
        }

        const userUpgrades = await Promise.all(
          tokenIds.map(async (tokenIdRaw) => {
            const tokenId = tokenIdRaw.toString();
            const upgradeData = await contracts.upgradeNFT.getUpgradeDetails(tokenId);
            return {
              tokenId,
              upgradeName: upgradeData.upgradeName,
              statAffected: upgradeData.statAffected,
              boostAmount: Number(upgradeData.boostAmount),
              rarity: Number(upgradeData.rarity),
            };
          })
        );

        userUpgrades.sort((a, b) => Number(b.tokenId) - Number(a.tokenId));
        setUpgrades(userUpgrades);
        setSelectedUpgradeId((current) => current ?? userUpgrades[0]?.tokenId ?? null);
      }

      if (contracts.chestNFT) {
        try {
          let chestIds: any[] = [];
          try {
            chestIds = await contracts.chestNFT.getChestsByOwner(address);
          } catch {
            const balance = Number(await contracts.chestNFT.balanceOf(address));
            chestIds = await Promise.all(
              Array.from({ length: balance }, (_, i) =>
                contracts.chestNFT.tokenOfOwnerByIndex(address, i)
              )
            );
          }

          const closedChests = (
            await Promise.all(
              chestIds.map(async (tokenIdRaw) => {
                const tokenId = tokenIdRaw.toString();
                const details = await contracts.chestNFT.getChestDetails(tokenId);
                return {
                  tokenId,
                  chestTier: Number(details.chestTier),
                  mintBlock: Number(details.mintBlock || 0),
                  isOpened: Boolean(details.isOpened),
                };
              })
            )
          ).filter((chest) => !chest.isOpened);

          closedChests.sort((a, b) => Number(b.tokenId) - Number(a.tokenId));
          setChests(closedChests);
        } catch (chestError) {
          console.error('Failed to load chests:', chestError);
          setChests([]);
        }
      } else {
        setChests([]);
      }

      if (contracts.nodeNFT) {
        let tokenIds: any[] = [];
        try {
          tokenIds = await contracts.nodeNFT.getNodesByOwner(address);
        } catch {
          const nodeBalance = Number(await contracts.nodeNFT.balanceOf(address));
          tokenIds = await Promise.all(
            Array.from({ length: nodeBalance }, (_, i) =>
              contracts.nodeNFT.tokenOfOwnerByIndex(address, i)
            )
          );
        }

        const userNodes = await Promise.all(
          tokenIds.map(async (tokenIdRaw) => {
            const tokenId = tokenIdRaw.toString();
            const nodeStats = await contracts.nodeNFT.getNodeStats(tokenId);
            return {
              tokenId,
              hashPower: Number(nodeStats.hashPower),
              bandwidth: Number(nodeStats.bandwidth),
              uptime: Number(nodeStats.uptime),
              lastClaimTime: Number(nodeStats.lastClaimTime),
              totalEarned: Number(nodeStats.totalEarned),
              tier: Number(nodeStats.tier),
              isActive: nodeStats.isActive,
            };
          })
        );

        userNodes.sort((a, b) => Number(a.tokenId) - Number(b.tokenId));
        setNodes(userNodes);
        setTargetNodeId((current) => {
          if (current && userNodes.some((node) => node.tokenId === current)) return current;
          if (initialSelectedNodeId && userNodes.some((node) => node.tokenId === initialSelectedNodeId)) {
            return initialSelectedNodeId;
          }
          return userNodes[0]?.tokenId ?? null;
        });
      }
    } catch (error) {
      console.error('Failed to load inventory:', error);
      notify.error(error, 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const openClosedChest = async (chest: ClosedChest) => {
    if (!contracts.gameLogic || !signer?.provider) return;
    if (isWrongNetwork || !contractsReady) {
      notify.error('Switch to the correct Ethereum Classic network first');
      return;
    }

    try {
      setOpeningChestId(chest.tokenId);
      toast.loading('Waiting for reveal block...', { id: 'open-chest' });
      await waitUntilBlockAfter(signer.provider, chest.mintBlock);
      toast.loading('Opening chest...', { id: 'open-chest' });
      const tx = await contracts.gameLogic.openChest(chest.tokenId);
      await tx.wait();
      toast.success(`Chest ${chest.tokenId} opened`, { id: 'open-chest' });
      window.dispatchEvent(new CustomEvent('itemPurchased', { detail: { type: 'chest', success: true } }));
      await loadInventory();
    } catch (error: any) {
      console.error('Failed to open chest:', error);
      notify.error(error, 'Could not open chest', { id: 'open-chest' });
    } finally {
      setOpeningChestId(null);
    }
  };

  const performUpgrade = async (upgrade: Upgrade, nodeId: string) => {
    if (!contracts.gameLogic) return;
    if (isWrongNetwork || !contractsReady) {
      notify.error('Switch to the correct Ethereum Classic network first');
      return;
    }

    try {
      setLoading(true);
      const tx = await contracts.gameLogic.applyUpgrade(nodeId, upgrade.tokenId);
      await tx.wait();
      notify.success(`Upgrade applied to node ${nodeId}`);
      await loadInventory();
    } catch (error: any) {
      console.error('Failed to apply upgrade:', error);
      notify.error(error, 'Could not apply upgrade');
    } finally {
      setLoading(false);
    }
  };

  const applyUpgrade = async (upgrade: Upgrade) => {
    if (!targetNodeId) {
      notify.info('Select a node in inventory first');
      return;
    }

    await performUpgrade(upgrade, targetNodeId);
  };

  const getRarityName = (rarity: number) => {
    const names: Record<number, string> = {
      1: t('rarity.common'),
      2: t('rarity.uncommon'),
      3: t('rarity.rare'),
      4: t('rarity.epic'),
      5: t('rarity.legendary'),
    };

    return names[rarity] || t('rarity.unknown');
  };

  const getStatIcon = (statType: string) => {
    const icons: Record<string, string> = {
      hashPower: 'CPU',
      bandwidth: 'NET',
      uptime: 'UP',
    };

    return icons[statType] || '???';
  };

  const getStatName = (statType: string) => {
    const names: Record<string, string> = {
      hashPower: t('inventory.hash_power'),
      bandwidth: t('inventory.bandwidth'),
      uptime: t('inventory.uptime'),
    };

    return names[statType] || statType;
  };

  const selectedNode = nodes.find((node) => node.tokenId === targetNodeId) ?? null;

  const filteredNodes = nodes.filter((node) => {
    const query = nodeSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      node.tokenId.toLowerCase().includes(query) ||
      `node-${node.tokenId}`.toLowerCase().includes(query) ||
      `tier ${node.tier}`.toLowerCase().includes(query) ||
      (node.isActive ? 'online' : 'offline').includes(query)
    );
  });

  const filteredUpgrades = upgrades.filter((upgrade) => {
    const query = upgradeSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      upgrade.tokenId.toLowerCase().includes(query) ||
      upgrade.upgradeName.toLowerCase().includes(query) ||
      getStatName(upgrade.statAffected).toLowerCase().includes(query) ||
      getRarityName(upgrade.rarity).toLowerCase().includes(query)
    );
  });

  return (
    <div className="inventory-panel">
      <div className="inventory-header">
        <div className="inventory-heading">
          <span className="inventory-kicker">GEAR</span>
          <div>
            <div className="inventory-title">{t('inventory.title')}</div>
            <div className="inventory-subtitle">Apply upgrades · open sealed chests</div>
          </div>
        </div>
        <div className="inventory-header-stats">
          <span className="inventory-stat-chip">
            <em>Upgrades</em>
            {loading ? '…' : upgrades.length}
          </span>
          <span className="inventory-stat-chip">
            <em>Chests</em>
            {loading ? '…' : chests.length}
          </span>
          <span className="inventory-stat-chip">
            <em>Nodes</em>
            {loading ? '…' : nodes.length}
          </span>
          <button type="button" className="inventory-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </div>

      <div className="inventory-toolbar">
        <div className="inventory-toolbar-copy">
          <div className="inventory-toolbar-label">Target Node</div>
          <div className="inventory-toolbar-value">
            {selectedNode ? `NODE-${selectedNode.tokenId}` : 'Select a node below'}
          </div>
        </div>
        {selectedNode ? (
          <div className="inventory-toolbar-stats">
            <span>{formatHashPower(selectedNode.hashPower)}</span>
            <span>{formatBandwidth(selectedNode.bandwidth)}</span>
            <span>{formatUptime(selectedNode.uptime)}</span>
          </div>
        ) : (
          <div className="inventory-toolbar-hint">Pick a node to apply upgrades</div>
        )}
      </div>

      <div className="inventory-fee-strip" aria-live="polite">
        {reactivationFee != null || protocolFeeBps != null ? (
          <>
            {reactivationFee != null && (
              <span>
                Reactivation may cost <strong>{reactivationFee}</strong> ETCPOWv2
              </span>
            )}
            {protocolFeeBps != null && (
              <span>
                Protocol fee <strong>{(protocolFeeBps / 100).toFixed(2)}%</strong>
              </span>
            )}
          </>
        ) : (
          <span>
            Reactivation / protocol fees appear here when contract methods are live.
          </span>
        )}
      </div>

      <div className="inventory-chests-strip">
        <div className="inventory-section-copy">
          <span className="inventory-section-title">Closed Chests</span>
          <span className="inventory-section-subtitle">
            Wait ≥1 block past mint, then open for an upgrade
          </span>
        </div>
        {loading ? (
          <div className="inventory-chests-grid">
            <div className="inventory-chest-skeleton" />
            <div className="inventory-chest-skeleton" />
          </div>
        ) : chests.length > 0 ? (
          <div className="inventory-chests-grid">
            {chests.map((chest) => (
              <div key={chest.tokenId} className={`inventory-chest-card tier-${chest.chestTier}`}>
                <div>
                  <div className="inventory-chest-meta">
                    <span className={`shop-tier-badge tier-${chest.chestTier}`}>T{chest.chestTier}</span>
                    <span>CHEST-{chest.tokenId}</span>
                  </div>
                  <div className="inventory-chest-block">
                    Mint #{chest.mintBlock || '—'} · open after next block
                  </div>
                </div>
                <button
                  type="button"
                  className="hacker-button small success"
                  disabled={openingChestId === chest.tokenId || isWrongNetwork || !contractsReady}
                  onClick={() => void openClosedChest(chest)}
                >
                  {openingChestId === chest.tokenId ? 'OPENING…' : 'OPEN'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="inventory-chests-empty">No sealed chests — buy one in Shop</div>
        )}
      </div>

      <div className="inventory-body">
        <aside className="node-selector-section">
          <div className="node-selector-header">
            <div>
              <span className="node-selector-title">Choose where upgrades go</span>
              <span className="node-selector-hint">Selection stays inside inventory</span>
            </div>
            <div className="inventory-search-wrap">
              <input
                type="text"
                value={nodeSearch}
                onChange={(event) => setNodeSearch(event.target.value)}
                placeholder="Search nodes"
                className="inventory-search-input"
              />
            </div>
          </div>

          <div className="node-selector-grid">
            {loading ? (
              <>
                <div className="node-target-skeleton" />
                <div className="node-target-skeleton" />
              </>
            ) : nodes.length === 0 ? (
              <div className="node-selector-empty">No nodes available yet</div>
            ) : filteredNodes.length === 0 ? (
              <div className="node-selector-empty">No matching nodes</div>
            ) : (
              filteredNodes.map((node) => {
                const isSelected = targetNodeId === node.tokenId;

                return (
                  <button
                    key={node.tokenId}
                    type="button"
                    className={`node-target-card tier-${node.tier} ${isSelected ? 'selected' : ''}`}
                    onClick={() => setTargetNodeId(node.tokenId)}
                  >
                    <div className="node-target-top">
                      <span className="node-target-id">NODE-{node.tokenId}</span>
                      <span className={`node-target-status ${node.isActive ? 'online' : 'offline'}`}>
                        {node.isActive ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </div>
                    <div className="node-target-tier">
                      <span className={`shop-tier-badge tier-${node.tier}`}>T{node.tier}</span>
                    </div>
                    <div className="node-target-stats">
                      <span>{formatHashPower(node.hashPower)}</span>
                      <span>{formatBandwidth(node.bandwidth)}</span>
                      <span>{formatUptime(node.uptime)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <div className="inventory-content">
          <div className="inventory-content-header">
            <div className="inventory-section-copy">
              <span className="inventory-section-title">Upgrade Inventory</span>
              <span className="inventory-section-subtitle">
                {filteredUpgrades.length} visible of {upgrades.length}
              </span>
            </div>
            <div className="inventory-search-wrap">
              <input
                type="text"
                value={upgradeSearch}
                onChange={(event) => setUpgradeSearch(event.target.value)}
                placeholder="Search upgrades"
                className="inventory-search-input"
              />
            </div>
          </div>

          <div className="inventory-grid">
            {loading ? (
              <>
                <div className="upgrade-card-skeleton" />
                <div className="upgrade-card-skeleton" />
                <div className="upgrade-card-skeleton" />
              </>
            ) : upgrades.length === 0 ? (
              <div className="empty-inventory">
                <div className="empty-message">
                  <div className="empty-icon-container">
                    <span className="empty-icon">+</span>
                  </div>
                  <h3 className="empty-title">{t('inventory.no_upgrades')}</h3>
                  <p className="empty-description">{t('inventory.no_upgrades_desc')}</p>
                  <div className="empty-actions">
                    <div className="action-hint">
                      <span className="hint-icon">SHOP</span>
                      <span className="hint-text">Buy chests in the shop</span>
                    </div>
                    <div className="action-hint">
                      <span className="hint-icon">OPEN</span>
                      <span className="hint-text">Open them after the next block</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : filteredUpgrades.length === 0 ? (
              <div className="empty-inventory compact-empty">
                <div className="empty-message">
                  <h3 className="empty-title">NO MATCHING UPGRADES</h3>
                  <p className="empty-description">Try another search term</p>
                </div>
              </div>
            ) : (
              filteredUpgrades.map((upgrade) => (
                <div
                  key={upgrade.tokenId}
                  className={`inventory-item upgrade-item rarity-edge-${upgrade.rarity} ${
                    selectedUpgradeId === upgrade.tokenId ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedUpgradeId(upgrade.tokenId)}
                >
                  <div className="item-header">
                    <span className="item-id">
                      {t('inventory.upgrade_id')}
                      {upgrade.tokenId}
                    </span>
                    <div className="item-meta">
                      <span className={`item-rarity rarity-${upgrade.rarity}`}>
                        {getRarityName(upgrade.rarity)}
                      </span>
                      {upgrade.boostAmount > 100 && (
                        <span
                          className="item-warning"
                          title="This upgrade has a high boost value and may exceed node limits"
                        >
                          HIGH BOOST
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="item-summary">
                    <span className="stat-icon">{getStatIcon(upgrade.statAffected)}</span>
                    <div className="item-name-block">
                      <div className="item-name">{upgrade.upgradeName}</div>
                      <div className="item-boost-hero">+{upgrade.boostAmount}</div>
                    </div>
                  </div>

                  <div className="item-details">
                    <div className="item-data-grid">
                      <div className="item-effect">
                        <span className="effect-label">{t('inventory.stat_type')}</span>
                        <span className="effect-value">{getStatName(upgrade.statAffected)}</span>
                      </div>
                      <div className="item-boost">
                        <span className="boost-label">Boost</span>
                        <span className="boost-value">+{upgrade.boostAmount}</span>
                      </div>
                      <div className="item-target">
                        <span className="target-label">Applying to</span>
                        <span className="target-value">
                          {selectedNode ? `NODE-${selectedNode.tokenId}` : 'Select node'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="item-status">
                    <span className="status-text">{t('inventory.available')}</span>
                  </div>

                  <div className="item-actions">
                    <button
                      type="button"
                      className={`hacker-button small ${targetNodeId ? '' : 'disabled'}`.trim()}
                      onClick={(event) => {
                        event.stopPropagation();
                        void applyUpgrade(upgrade);
                      }}
                      disabled={!targetNodeId}
                    >
                      {targetNodeId ? `${t('inventory.apply')} TO NODE-${targetNodeId}` : t('inventory.apply')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
